import { useState, useMemo } from "react";
import { FiInbox } from "react-icons/fi";
import * as S from "./styles";
import { Modal } from "../../../components/Atoms/Modal";
import TransactionDetail from "./components/TransactionDetail";
import PaymentsTable from "./components/PaymentsTable";
import Button from "../../../components/Atoms/Button";
import { usePaymentBusiness } from "./hooks/usePaymentBusiness";
import { PageHeader, EmptyState, ErrorState, SkeletonTable } from "../../../components/Admin";
import { ListContainer } from "../../_layout/AdminLayout.styles";
import { PaymentModel, PaymentStatus } from "../../../models/PaymentModels";

const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Admin = () => {
  const {
    wallet,
    isLoading,
    loadError,
    payments,
    fetchMorePayments,
    fetchData,
    currentPage,
    maxPage,
  } = usePaymentBusiness();
  const [itemToOpen, setItemToOpen] = useState<{
    cartid: string;
    transactionid: string;
  }>();

  const handleSelectItem = (args: {
    cartid: string;
    transactionid: string;
  }) => {
    setItemToOpen(args);
  };

  // Métricas derivadas do que a carteira e os pagamentos já fornecem.
  const metrics = useMemo(() => {
    const totalPaid = payments
      .filter((p: PaymentModel) => p.status === PaymentStatus.PAID)
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalSales = payments.length;
    const awaiting = payments.filter(
      (p: PaymentModel) =>
        p.status === PaymentStatus.WAITING_PAYMENT ||
        p.status === PaymentStatus.WAITING_REPASS,
    ).length;
    return { totalPaid, totalSales, awaiting };
  }, [payments]);

  const showFetchMoreBtn = !!payments.length && maxPage > currentPage;

  const showInitialLoading = isLoading && !payments.length && !loadError;

  return (
    <>
      <PageHeader
        title="Minhas Vendas"
        subtitle="Carteira e pagamentos da escola."
      />

      {showInitialLoading ? (
        <SkeletonTable rows={4} columns={2} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={fetchData} />
      ) : (
        <>
          <S.WalletContainer>
            <S.WalletCard accent="gold">
              <S.WalletTitle>Saldo</S.WalletTitle>
              <S.WalletValue>
                <p>R$</p>
                {formatBRL(wallet.balance)}
              </S.WalletValue>
            </S.WalletCard>
            <S.WalletCard accent="amber">
              <S.WalletTitle>Disponível para retirada</S.WalletTitle>
              <S.WalletValue>
                <p>R$</p>
                {formatBRL(wallet.available)}
              </S.WalletValue>
            </S.WalletCard>
            <S.WalletCard>
              <S.WalletTitle>Recebido (pagos)</S.WalletTitle>
              <S.WalletValue>
                <p>R$</p>
                {formatBRL(metrics.totalPaid)}
              </S.WalletValue>
            </S.WalletCard>
            <S.WalletCard>
              <S.WalletTitle>Vendas (nesta página)</S.WalletTitle>
              <S.WalletValue>
                <p>R$</p>
                {metrics.totalSales}
              </S.WalletValue>
            </S.WalletCard>
          </S.WalletContainer>

          <ListContainer>
            {payments.length === 0 ? (
              <EmptyState
                icon={<FiInbox size={40} />}
                title="Nenhum pagamento ainda"
                description="Assim que houver vendas, elas aparecerão aqui."
              />
            ) : (
              <PaymentsTable
                data={payments}
                currentIndex={currentPage}
                lastIndex={maxPage}
                onSelectItem={handleSelectItem}
              />
            )}
          </ListContainer>
        </>
      )}

      <Modal isOpen={itemToOpen !== undefined}>
        {itemToOpen && (
          <TransactionDetail
            {...itemToOpen}
            onClose={() => setItemToOpen(undefined)}
          />
        )}
      </Modal>
      {showFetchMoreBtn && (
        <Button onClick={fetchMorePayments} loading={isLoading}>
          Carregar mais ...
        </Button>
      )}
    </>
  );
};

export default Admin;
